import React from 'react'
import { Button } from 'antd'
import { IWogPay } from './useWogPay'
import { formatLiters } from './format'

/*
    The footer is one button pair per screen, so it is written as one early
    return per screen rather than an array the modal assembles. antd renders
    whatever it is given straight into `.ant-modal-footer`, so a fragment lays
    the buttons out exactly as an array would.
 */

interface IFRefuelFooter {
  pay: IWogPay
  /**
   * Whether the flow can be dismissed. The modal can; the page for app users
   * is the whole page, so its footer carries no «Закрити» or «Скасувати».
   */
  closable?: boolean
}

/**
 * A failure that the manager can fix offers, next to «Назад», one button that
 * goes straight to the field at fault — which screen that is, is `recover`'s
 * business.
 */
const RECOVER_LABELS: { [action: string]: string } = {
  amount: 'Змінити кількість',
  params: 'Змінити параметри',
  station: 'Обрати іншу АЗК',
}

const RefuelFooter: React.FC<IFRefuelFooter> = ({ pay, closable = true }) => {
  const close = closable ? <Button onClick={pay.close}>Закрити</Button> : null

  if (pay.step === 'station') {
    if (pay.dataError) {
      return (
        <>
          {close}
          <Button type="primary" onClick={pay.retryFuelData}>
            Спробувати ще раз
          </Button>
        </>
      )
    }

    return (
      <>
        {closable && <Button onClick={pay.close}>Скасувати</Button>}
        <Button type="primary" disabled={!pay.station} onClick={pay.next}>
          Далі
        </Button>
      </>
    )
  }

  if (pay.step === 'amount') {
    if (pay.configError) {
      return (
        <>
          <Button onClick={pay.back}>Назад</Button>
          <Button type="primary" onClick={pay.retryConfiguration}>
            Спробувати ще раз
          </Button>
        </>
      )
    }

    if (pay.blocked) {
      return (
        <>
          {close}
          <Button type="primary" onClick={pay.chooseOtherStation}>
            Обрати іншу АЗК
          </Button>
        </>
      )
    }

    // What the account is about to be debited sits next to the button that
    // dispenses the fuel; the driver and the station are named in the header.
    const fuel = pay.liters > 0 ? pay.fuel : undefined

    return (
      <>
        {fuel && (
          <span className="refuel-foot">
            {`Буде списано ${formatLiters(pay.liters)} л ${fuel.label}`}
          </span>
        )}
        <Button onClick={pay.back}>Назад</Button>
        <Button
          type="primary"
          loading={pay.submitting}
          disabled={!pay.canSubmit}
          onClick={pay.submit}
        >
          {fuel ? `Заправити · ${formatLiters(pay.liters)} л` : 'Заправити'}
        </Button>
      </>
    )
  }

  const { result } = pay
  if (!result) return close

  if (result.ok) {
    return (
      <>
        {close}
        <Button type="primary" onClick={pay.startOver}>
          Нова заправка
        </Button>
      </>
    )
  }

  // Paced and capped — FillUpCar has no idempotency key.
  const retry = result.action === 'retry' && pay.canRetry
  const recover = !!RECOVER_LABELS[result.action]

  return (
    <>
      {close}
      {/* One step back with everything kept — another fuel, pump or figure, or on to the station and account. */}
      <Button onClick={pay.back}>Назад</Button>
      {retry && (
        <Button
          type="primary"
          loading={pay.submitting}
          disabled={pay.cooldown > 0}
          onClick={pay.submit}
        >
          {pay.cooldown > 0 ? `Повторити (${pay.cooldown})` : 'Повторити'}
        </Button>
      )}
      {recover && (
        <Button type="primary" onClick={pay.recover}>
          {RECOVER_LABELS[result.action]}
        </Button>
      )}
      {/* Nothing to retry or fix: the page offers a fresh start where the modal has «Закрити». */}
      {!closable && !retry && !recover && (
        <Button type="primary" onClick={pay.startOver}>
          Нова заправка
        </Button>
      )}
    </>
  )
}

export default RefuelFooter
